﻿import _ from "underscore";
import {LocationSearchParams} from "../hooks/search/useLocationSearchParams";
import { PlacePrediction } from "../components/placesAutocomplete/interfaces";

/**
 * Parse the lat/lng URL params into a numeric LatLngLiteral.
 *
 * Returns undefined unless BOTH coordinates are present and parse to finite
 * numbers — callers use this to decide whether the URL carries a usable
 * map-viewport center (e.g. coordinates written while dragging the map).
 *
 * @param params location params carrying optional `lat`/`lng` strings
 */
export const parseLatLngParams = (
    params: Pick<LocationSearchParams, 'lat' | 'lng'> | undefined | null
): google.maps.LatLngLiteral | undefined => {
    if (!params?.lat || !params?.lng) return undefined;
    const lat = parseFloat(params.lat);
    const lng = parseFloat(params.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
    return { lat, lng };
};

export const getQueryParamsFromPlace = (place: google.maps.GeocoderResult | undefined | null, name: 'short_name' | 'long_name' = 'short_name', includeStreetName: boolean = true): LocationSearchParams | undefined => {
    if (!place) return;

    const addressComponents = place.address_components;
    const street_number = _.find(addressComponents, ac => _.contains(ac.types, 'street_number'))?.[name] || ''
    const street_name = _.find(addressComponents, ac => _.contains(ac.types, 'route'))?.[name] || ''
    // Always the full city name: Google's short name for some cities is an
    // abbreviation (Kansas City → "KCMO", San Francisco → "SF") that leaked into
    // URLs, page titles and canonicals (securspace-ui#3048).
    const city = _.find(addressComponents, ac => _.contains(ac.types, 'locality'))?.long_name || ''
    const state = _.find(addressComponents, ac => _.contains(ac.types, 'administrative_area_level_1'))?.[name] || ''
    const country = _.find(addressComponents, ac => _.contains(ac.types, 'country'))?.[name] || ''
    const place_id = _.find(addressComponents, ac => _.contains(ac.types, 'place_id'))?.[name] || ''

    // Extract lat/lng from the geometry if available
    const geometry = place.geometry;
    const lat = geometry?.location ? String(typeof geometry.location.lat === 'function' ? geometry.location.lat() : geometry.location.lat) : undefined;
    const lng = geometry?.location ? String(typeof geometry.location.lng === 'function' ? geometry.location.lng() : geometry.location.lng) : undefined;

    if (includeStreetName) {
        return {
            street_number,
            street_name,
            city,
            state,
            country,
            place_id,
            lat,
            lng
        }
    }
    return {
        city,
        state,
        country,
        place_id,
        lat,
        lng
    }
}

/**
 * Extracts location parameters from a Google Places Autocomplete prediction without geocoding.
 * Parses the structured_formatting fields to extract street number, street name, city, and state.
 * 
 * Handles edge cases:
 * - Street numbers with letters (e.g., "123A", "456-B")
 * - Street names with numbers (e.g., "Route 66", "2nd Avenue")
 * - Multi-word city and state names (e.g., "Los Angeles", "New Mexico")
 * 
 * @param pacItem - Google Places Autocomplete prediction with structured_formatting
 * @returns LocationSearchParams with street_number, street_name, city, and state, or undefined if invalid
 * 
 * @example
 * const params = getQueryParamsFromPacItem(pacItem);
 * // Returns: { street_number: "914", street_name: "Calhoun Avenue", city: "Juneau", state: "AK" }
 */
export const getQueryParamsFromPacItem = (pacItem: PlacePrediction): LocationSearchParams | undefined => {
    if (!pacItem?.structured_formatting) {
        return undefined;
    }

    const { structured_formatting, place_id } = pacItem;
    const { main_text, secondary_text } = structured_formatting;
    
    if (!main_text || !secondary_text) {
        return undefined;
    }

    // Parse main_text to extract street_number and street_name
    // main_text format is typically "123 Main St" or "123A Oak Avenue"
    const mainTextParts = main_text.trim().split(/\s+/);
    let street_number = '';
    let street_name = '';

    let city = '';
    let state = '';
    let country = '';

    // Parse secondary_text to extract city and state
    // secondary_text format is typically "City, ST, Country" or "City, State, Country"
    // We want to extract the city (first part before comma) and state (second part before comma)
    const secondaryParts = secondary_text.split(',').map((part: string) => part.trim());

    if (structured_formatting.full_address) {
        // First part is usually the street number (may include letters like "123A", "456-B")
        const firstPart = mainTextParts[0];
        
        // Check if first part contains only digits or digits with trailing letters (e.g., "123A", "456B")
        // This regex matches: one or more digits, optionally followed by letters
        if (/^\d+[a-zA-Z]*$/i.test(firstPart)) {
            street_number = firstPart;
            // Rest is street name (handles multi-word names like "West Main Street")
            street_name = mainTextParts.slice(1).join(' ');
        } else {
            // If first part doesn't match number pattern, entire main_text is street name
            street_name = main_text;
        }
        city = secondaryParts[0]
        state = secondaryParts[1]
        country = secondaryParts[2]
    } else {
        // if there is no street address, city is the main_text
        city = main_text
        // Second part is state (handles both short codes like "CA" and long names)
        // Usually short codes like "AK", "NY" but can also be full state names
        state = secondaryParts[0];
        country = secondaryParts[1];

    }

    // Return params if we have at least city and state
    if (city && state) {
        return {
            street_number,
            street_name,
            city,
            state,
            place_id,
            country
        };
    }

    return undefined;
};
